import React, { useCallback } from 'react';
import { useFlashFlag } from '@/hooks/useFlashFlag';
import { Link } from '@tanstack/react-router';
import { Copy, FileText, Mail, ScrollText, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  campaignConfig,
  customerConfig,
  legalConfig,
} from '../../config/customerConfig';

const Support: React.FC = () => {
  const appName = customerConfig.appTitle || legalConfig.companyName;
  const contactEmail =
    campaignConfig.supportEmail ||
    campaignConfig.contactEmail ||
    campaignConfig.emailFromAddress ||
    legalConfig.contactEmail;

  const [copied, flashCopied] = useFlashFlag(1500);
  const copyEmail = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(contactEmail);
      flashCopied();
    } catch {
      // Clipboard permission denied — the mailto link is still the primary path.
    }
  }, [contactEmail, flashCopied]);

  const faqs = [
    {
      question: `What is ${appName}?`,
      answer: (
        <>
          {appName} is a campaign management platform that helps political
          campaigns organize voter outreach, manage canvassing operations, and
          coordinate volunteers.
        </>
      ),
    },
    {
      question: 'How do I create an account?',
      answer: (
        <>
          Campaign managers can create a new campaign and account on the{' '}
          <Link
            to="/get-started"
            className="text-primary underline-offset-2 hover:underline"
          >
            Get started page
          </Link>
          . Volunteers join a campaign by invitation from their campaign
          administrator.
        </>
      ),
    },
    {
      question: 'How do I reset my password?',
      answer: (
        <>
          Use the &ldquo;Forgot Password&rdquo; link on the{' '}
          <Link
            to="/login"
            className="text-primary underline-offset-2 hover:underline"
          >
            login page
          </Link>{' '}
          to receive a password reset email.
        </>
      ),
    },
    {
      question: 'How do I delete my account?',
      answer: (
        <>
          To request account deletion, email{' '}
          <a
            href={`mailto:${contactEmail}`}
            className="break-all text-primary underline-offset-2 hover:underline"
          >
            {contactEmail}
          </a>
          . We&rsquo;ll process your request and remove your personal data.
        </>
      ),
    },
  ] as const;

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl px-6 py-12 sm:py-16">
        <header className="mb-10">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Help &amp; support
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Questions about {appName}? Reach out below or check the FAQ.
          </p>
        </header>

        {/* Primary path: email support. Contact card carries the CTA weight;
            everything below it is scannable reference material. */}
        <Card className="mb-10 p-5">
          <div className="flex items-start gap-4">
            <div
              aria-hidden
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"
            >
              <Mail className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-foreground">
                Contact support
              </h2>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
                <a
                  href={`mailto:${contactEmail}`}
                  className="break-all text-sm font-medium text-primary underline-offset-2 hover:underline"
                >
                  {contactEmail}
                </a>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={copyEmail}
                  aria-live="polite"
                  className="h-7 gap-1 px-2 text-xs"
                >
                  <Copy className="h-3.5 w-3.5" aria-hidden />
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* FAQ — semantic <dl>, all-open (only 4 items — click-to-reveal
            adds friction here, not value), subtle dividers instead of a
            card-per-question grid (the identical-card slop). */}
        <section aria-labelledby="faq-heading" className="mb-12">
          <h2
            id="faq-heading"
            className="mb-6 text-base font-semibold text-foreground"
          >
            Frequently asked questions
          </h2>
          <dl className="divide-y divide-border">
            {faqs.map((faq, i) => (
              <div
                key={faq.question}
                className={i === 0 ? 'pb-6' : 'py-6 last:pb-0'}
              >
                <dt className="text-[15px] font-medium text-foreground">
                  {faq.question}
                </dt>
                <dd className="mt-2 max-w-prose text-sm leading-relaxed text-muted-foreground">
                  {faq.answer}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Legal footer — muted, iconified, routed inside the app so back-nav
            returns to /support. */}
        <Separator className="mb-5" />
        <footer className="flex flex-wrap items-center justify-between gap-4">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            Legal
          </span>
          <nav
            aria-label="Legal documents"
            className="flex flex-wrap items-center gap-x-5 gap-y-2"
          >
            <Link
              to="/eula"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <FileText className="h-3.5 w-3.5" aria-hidden />
              EULA
            </Link>
            <Link
              to="/privacy-policy"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ScrollText className="h-3.5 w-3.5" aria-hidden />
              Terms of Service
            </Link>
          </nav>
        </footer>
      </div>
    </div>
  );
};

export default Support;
